import {
    CategoryAnswer, ICategoryAnswer, ICompletedCategoryAnswer,
    ICompletedCategoryQuestion,
    ICompletedMultipleChoiceAnswer,
    ICompletedMultipleChoiceQuestion, ICompletedOrderAnswer, ICompletedOrderQuestion
} from "@/Core/Interactions";
import {MultilingualText, toLocalString, tryLocalString} from "@/i18n/MultilingualText";
import {ICategoryQuestion, IMultipleChoiceQuestion, IOrderQuestion} from "@/CreatorTools/Model/Questions";

export interface IQuestionContext {
    questionType: "choice"|"sequence"|"category";
    code: string;
    questionText: MultilingualText;
    questionNumber: number;
    minAnswers: number;
    maxAnswers: number;
    maxAttempts: number;
    feedback: MultilingualText;
    categories: {[category: string]: MultilingualText};
    answers: IQuestionAnswerContext[];
}

export interface IQuestionAnswerContext {
    code: string;
    answerText: MultilingualText;
    isCorrect: boolean|null;
    correctPosition: number|null;
    correctCategory: string|null;
    answerFeedback: MultilingualText|null;
}

export function markChoiceQuestion(choiceQuestion: IMultipleChoiceQuestion, chosenCodes: string[]): ICompletedMultipleChoiceQuestion {
    let correctAnswers = choiceQuestion.answers.filter(a => a.isCorrect);
    let numRequiredCorrectAnswers = Math.min(correctAnswers.length, choiceQuestion.maxAnswers);
    let givenAnswers: ICompletedMultipleChoiceAnswer[] = [];

    for (let answerCode of chosenCodes) {
        let answer = choiceQuestion.answers.find(a => a.id === answerCode);
        if (answer == null)
            throw "Could not find answer with code: " + answerCode;

        givenAnswers.push({
            questionAnswerCode: answerCode,
            answerText: toLocalString(answer.text),
            isCorrect: <boolean> answer.isCorrect,
            mark: answer.isCorrect ? 1 / numRequiredCorrectAnswers : 0,
            feedback: null,
        });
    }

    let isCorrect = (chosenCodes.length == correctAnswers.length || chosenCodes.length === choiceQuestion.maxAnswers)
        && givenAnswers.every(a => a.isCorrect);

    return {
        questionType: "choice",
        questionText: toLocalString(choiceQuestion.text),
        maxAttempts: choiceQuestion.maxAttempts,
        maxAnswers: choiceQuestion.maxAnswers,
        minAnswers: choiceQuestion.minAnswers,
        feedback: toLocalString(choiceQuestion.feedback),
        questionCode: choiceQuestion.id,
        correctAnswers: choiceQuestion.answers.filter(a => a.isCorrect).map(a => ({
            answerText: toLocalString(a.text),
            code: a.id,
            isCorrect: <boolean> a.isCorrect
        })),
        givenAnswers: givenAnswers,
        isCorrect: isCorrect,
        totalMark: givenAnswers.reduce((pv, cv) => pv + cv.mark, 0)
    };
}


export function markCategoryQuestion(categoryQuestion: ICategoryQuestion,
                              placedCategories: CategoryAnswer[]) :ICompletedCategoryQuestion {
    let givenAnswers: ICompletedCategoryAnswer[] = [];
    let correctAnswers: ICategoryAnswer[] = [];

    for (let placedAnswer of placedCategories) {
        let answer = categoryQuestion.items.find(a => a.id === placedAnswer.answerItem);
        if (answer == null)
            throw "Could not find answer with code: " + placedAnswer.answerItem;

        let correctCategory = categoryQuestion.categories.find(c => c.items.some(i => i === placedAnswer.answerItem));
        if (correctCategory != null) {
            correctAnswers.push({
                questionCode: categoryQuestion.id,
                answerCode: answer.id,
                itemText: toLocalString(answer.text),
                answerFeedback: answer.feedback ? tryLocalString(answer.feedback) : null,
                correctCategory: <string> correctCategory.id,
            })
        }

        givenAnswers.push({
            isCorrect: placedAnswer.category === correctCategory?.id,
            answerText: toLocalString(answer.text),
            questionAnswerCode: placedAnswer.answerItem,
            questionCode: categoryQuestion.id,
            category: placedAnswer.category,
            mark: 0
        });
    }

    let isCorrect = givenAnswers.length === correctAnswers.length && givenAnswers.every(a => a.isCorrect);

    for (let a of givenAnswers) {
        a.mark = a.isCorrect ? 1 / givenAnswers.length : 0;
    }

    return {
        questionType: "category",
        questionText: toLocalString(categoryQuestion.text),
        maxAttempts: categoryQuestion.maxAttempts,
        maxAnswers: categoryQuestion.maxAnswers,
        minAnswers: categoryQuestion.minAnswers,
        feedback: toLocalString(categoryQuestion.feedback),
        questionCode: categoryQuestion.id,
        correctAnswers: correctAnswers,
        givenAnswers: givenAnswers,
        isCorrect: isCorrect,
        totalMark: givenAnswers.reduce((pv, cv) => pv + cv.mark, 0)
    };
}

export function markSequenceQuestion(sequenceQuestion: IOrderQuestion, chosenOrder: string[]): ICompletedOrderQuestion {
    if (sequenceQuestion.patterns.length != 1)
        throw "Can only mark sequence questions with one correct pattern";

    let correctAnswers = sequenceQuestion.patterns[0].codes.map((a, i) => {
        let answer = sequenceQuestion.items.find(x => a === x.id);
        if (answer == null)
            throw "Could not find answer";

        return {
            questionCode: sequenceQuestion.id,
            answerCode: a,
            answerText: toLocalString(answer.text),
            code: answer.id,
            isCorrect: true,
            correctPosition: i
        }
    });

    let givenAnswers: ICompletedOrderAnswer[] = [];

    for (let i = 0; i < chosenOrder.length; i++) {
        let answerCode = chosenOrder[i];
        let answer = sequenceQuestion.items.find(a => a.id === answerCode);
        if (answer == null)
            throw "Could not find answer with code: " + answerCode;

        let correctAnswerPosition = correctAnswers.findIndex(a => a.code === answerCode);

        givenAnswers.push({
            position: i,
            questionAnswerCode: answerCode,
            answerText: toLocalString(answer.text),
            isCorrect: i === correctAnswerPosition,
            mark: 0
        });
    }

    let isCorrect = givenAnswers.length == correctAnswers.length && givenAnswers.every(a => a.isCorrect);
    for (let a of givenAnswers) {
        a.mark = isCorrect ? 1 / givenAnswers.length : 0;
    }

    return {
        questionType: "sequence",
        maxAnswers: sequenceQuestion.maxAnswers,
        minAnswers: sequenceQuestion.minAnswers,
        maxAttempts: sequenceQuestion.maxAttempts,
        questionText: toLocalString(sequenceQuestion.text),
        feedback: toLocalString(sequenceQuestion.feedback),
        questionCode: sequenceQuestion.id,
        correctAnswers: correctAnswers,
        givenAnswers: givenAnswers,
        isCorrect: isCorrect,
        totalMark: givenAnswers.reduce((pv, cv) => pv + cv.mark, 0)
    };
}